import { useEffect, useState } from 'react';
import { Link } from '../components/nav';
import { loadStats } from '../data/load';
import type { Stats } from '../data/types';

/** Dirección de contacto del proyecto. Un aviso legal sin canal de contacto no sirve de nada, así
 *  que vive aquí, en un solo sitio, y tiene que existir antes de publicar el sitio. */
const CONTACTO = 'hola@1884.es';

/** Aviso legal y privacidad (pendiente desde la fase 1, cerrado al desplegar en la fase F).
 *  Todo lo que se afirma aquí es comprobable en el propio sitio: no hay cuentas, ni cookies, ni
 *  analítica, ni formularios. Si algún día los hubiera, esta página tendría que cambiar primero. */
export function Legal() {
  // las cifras se leen de stats.json, no se escriben aquí: una página legal no puede envejecer mal
  const [stats, setStats] = useState<Stats | null>(null);
  useEffect(() => { loadStats().then(setStats).catch(() => setStats(null)); }, []);
  return (
    <div className="page legal">
      <h1>Aviso legal y privacidad</h1>

      <h2>Quién publica esto</h2>
      <p>
        «Castilla y León 1884: leer el territorio, recorrer el libro» es un recurso educativo abierto editado por
        Eugenio Serrano. No es una publicación oficial ni está respaldada por la Junta de Castilla y León, por el
        Instituto Geográfico Nacional ni por ninguna de las instituciones cuyas fuentes utiliza; son fuentes
        citadas, no patrocinadores.
      </p>
      <p>
        Para avisar de un error, discutir una correspondencia o pedir que se retire algo, escribe
        a <a href={`mailto:${CONTACTO}`}>{CONTACTO}</a>. Las correcciones se atienden y se documentan: cada
        cambio de una correspondencia queda registrado con su fecha y su motivo.
      </p>

      <h2>Datos personales</h2>
      <p>
        Esta aplicación no pide ni guarda datos personales. No hay cuentas, ni registro, ni formularios, ni cookies,
        ni analítica, ni publicidad, ni rastreadores de terceros. No se comparte nada con nadie, porque no hay nada
        que compartir.
      </p>
      <p>
        Lo único que se guarda es el progreso de los retos, y vive en el <em>almacenamiento local</em> del navegador
        de quien juega: no sale del dispositivo, no llega al servidor y se borra con el botón «Borrar mi progreso»
        de la página de <Link to="/retos">Retos</Link> o vaciando los datos del sitio en el navegador.
      </p>
      <p>
        Al cargar el mapa, el navegador pide las teselas al Instituto Geográfico Nacional —o a OpenStreetMap si el
        IGN no responde—, y esos servidores reciben la dirección IP, como en cualquier página con mapa. Lo mismo
        ocurre al seguir un enlace externo a Internet Archive, a Wikidata o al portal de datos abiertos de la Junta.
        La aplicación no envía a esos servicios nada más que la petición de la tesela o el propio clic.
      </p>
      <p>
        El servidor donde está alojado el sitio puede llevar sus propios registros de acceso, ajenos a este
        proyecto, como cualquier servidor web.
      </p>

      <h2>Derechos sobre los contenidos</h2>
      <p>
        El texto de José María Quadrado, <cite>Salamanca, Ávila y Segovia</cite> (Barcelona, Daniel Cortezo y C.ª,
        1884), y las imágenes del volumen están en dominio público. La digitalización procede de Internet Archive
        (ejemplar del Getty Research Institute).
      </p>
      <p>
        Los datos monumentales son de la Junta de Castilla y León («Relación de monumentos», CC BY 4.0). Las
        ubicaciones que no figuran en ese catálogo proceden de OpenStreetMap (© colaboradores de OpenStreetMap,
        ODbL 1.0), y por eso el conjunto de datos de lugares se publica bajo ODbL 1.0. La cartografía base es del
        Instituto Geográfico Nacional (CC BY 4.0 scne.es).
      </p>
      <p>
        La edición, las anotaciones, los itinerarios, los retos y los materiales de aula son de Eugenio Serrano,
        con licencia CC BY 4.0; el código, con licencia MIT. Se pueden reutilizar citando la procedencia y, en el
        caso del conjunto de datos, manteniendo la ODbL. El detalle está en <Link to="/proyecto/fuentes">Fuentes, método y créditos</Link>.
      </p>

      <h2>Exactitud y responsabilidad</h2>
      <p>
        Es una edición en construcción y lo dice en cada página donde importa. Las correspondencias entre lo que
        describe el libro y los edificios de hoy llevan su nivel de confianza y quién las revisó, y «no determinado»
        es un resultado legítimo que aparece en {stats ? `${stats.placesUndetermined} de los ${stats.places}` : 'una parte de los'} lugares publicados. Nada de lo que hay aquí sirve
        como documento acreditativo del estado, la protección o la titularidad de un bien.
      </p>
      <p>
        Los itinerarios son propuestas de lectura sobre el mapa. No se han recorrido a pie ni se ha comprobado sobre
        el terreno su accesibilidad o su seguridad, y así se advierte en cada uno. Quien los camine lo hace bajo su
        responsabilidad.
      </p>
      <p>
        El texto de 1884 se publica íntegro, sin censura, porque es la fuente. Contiene juicios y categorías propios
        de su época y de su autor —sobre las juderías, sobre la expulsión de 1492, sobre los moriscos— que no son los
        de esta edición. La guía docente explica cómo trabajarlos en clase.
      </p>

      <h2>Accesibilidad</h2>
      <p>
        Todo se puede usar con teclado, el texto se puede leer sin abrir el mapa y cada imagen del facsímil tiene su
        transcripción al lado. Las pruebas automáticas de accesibilidad se pasan en cada versión y no quedan
        incidencias críticas ni serias, pero faltan pruebas con lectores de pantalla reales. Si algo no funciona con
        tu configuración, escribe a <a href={`mailto:${CONTACTO}`}>{CONTACTO}</a> y se corrige.
      </p>
    </div>
  );
}
